import React, { useCallback, useMemo, useState } from 'react';
import type { SoQuyTienMatRequest, SoQuyTienMatRow, SoQuyTienMatSearchFormValues } from '@/types/schemas';
import { type CashBookDocumentEntryState, resolveCashBookDocumentEntry } from './utils/document-entry';
import { SoQuyTienMatProvider, useSoQuyTienMatContext } from './context';
import { LoadingOverlay, ErpDataTables } from '@/components/custom/erp';
import CashBookDocumentEntry from './components/DocumentEntry';
import { useDynamicSearch } from '@/hooks/use-dynamic-search';
import { ActionBar, InitialSearchDialog } from './components';
import { cashBookColumns } from './cols-definition';
import { QUERY_KEYS } from '@/constants';
import { useFormState } from './hooks';
import { useReport } from '@/hooks';

export default function CashBookPage() {
  const { getReport, isLoading, data, currentPage, handlePageChange, totalItems } = useReport<
    SoQuyTienMatRow,
    Partial<SoQuyTienMatRequest>
  >({
    endpoint: QUERY_KEYS.SO_QUY_TIEN_MAT,
    prefetch: false
  });

  const { showForm, handleOpenForm, handleCloseForm } = useFormState();
  const [searchDisplay, setSearchDisplay] = useState<SoQuyTienMatSearchFormValues>();
  const [requestParams, setRequestParams] = useState<Partial<SoQuyTienMatRequest>>({});
  const [documentEntry, setDocumentEntry] = useState<CashBookDocumentEntryState | null>(null);
  const { setExportBody, isExporting } = useSoQuyTienMatContext();
  const numericFields = useMemo(() => new Set(['ty_gia', 'ps_no', 'ps_co', 'du_tt']), []);
  const handleDocumentClick = useCallback((row: SoQuyTienMatRow) => {
    setDocumentEntry(resolveCashBookDocumentEntry(row));
  }, []);
  const columns = useMemo(() => cashBookColumns(handleDocumentClick), [handleDocumentClick]);
  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch: getReport,
    params: requestParams || {},
    onInitialSearch: async (body, onSearchFn) => {
      await onSearchFn(body);
      setExportBody(body);
      setDocumentEntry(null);
      handleCloseForm();
    },
    getColumns: () =>
      columns
        .filter(col => typeof col.field === 'string')
        .map(col => ({
          field: col.field,
          type: numericFields.has(col.field as string) ? 'number' : 'string'
        })),
    endpoint: QUERY_KEYS.SO_QUY_TIEN_MAT,
    pageSize: 50
  });

  const handleSubmit = async (formValues: SoQuyTienMatSearchFormValues) => {
    setSearchDisplay(formValues);
    const { tk, ...formData } = formValues;
    const body = { ...formData, tk: tk?.uuid || '' };
    setRequestParams(body);
    try {
      await handleInitialSearch(body);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseDocumentEntry = useCallback(() => {
    setDocumentEntry(null);
  }, []);

  const handleDocumentMutated = useCallback(async () => {
    if (hasFilterParams) {
      await handleRefreshClick();
      return;
    }

    if (Object.keys(requestParams || {}).length > 0) {
      await getReport(requestParams);
    }
  }, [getReport, handleRefreshClick, hasFilterParams, requestParams]);

  const resolvedRows = hasFilterParams ? searchState.searchResults : data;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedHandlePageChange = hasFilterParams ? handleSearchPageChange : handlePageChange;

  const tables = [
    {
      name: '',
      rows: resolvedRows,
      columns
    }
  ];
  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      {showForm && <InitialSearchDialog onClose={handleCloseForm} onSearch={handleSubmit} open />}

      {documentEntry && (
        <CashBookDocumentEntry
          document={documentEntry}
          onClose={handleCloseDocumentEntry}
          onMutated={handleDocumentMutated}
        />
      )}

      {!showForm && !documentEntry && (
        <div className='flex h-full min-h-0 w-full flex-col'>
          <ActionBar
            onSearchClick={handleOpenForm}
            onRefreshClick={handleRefreshClick}
            onEditPrintTemplateClick={() => {}}
            onFixedColumnsClick={() => {}}
            onPrintClick={() => {}}
            searchParams={searchDisplay}
          />
          <div className='relative min-h-0 flex-1 overflow-hidden'>
            <ErpDataTables
              tables={tables}
              pageSize={50}
              totalItems={resolvedTotalItems}
              currentPage={resolvedCurrentPage}
              onPageChange={resolvedHandlePageChange}
              onSearchChange={handleSearchChange}
              loading={isLoading || isSearching}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function CashBookPageContent() {
  return (
    <SoQuyTienMatProvider>
      <CashBookPage />
    </SoQuyTienMatProvider>
  );
}
